import { VStack } from '@genial/components';
import { Spacing } from '@genial/design-tokens';
import { ErrorListResponse, instanceOfErrorListResponse } from '@genial/services';
import { Utils } from '@genial/utils';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import React, { useEffect } from 'react';

import { useRecoilQuery } from '../../../../hooks/useRecoilQuery';
import { useToastCreator } from '../../../../hooks/useToastCreator';
import {
  SignedInNavigationScreenNavigationProp,
  SignedInRootStackParamList,
} from '../../../../routes/types';
import { useRedemptionActions } from '../../../../store/redemptionSolicitation/actions';
import { fixedIncomeWithdrawGetAsset } from '../../../../store/redemptionSolicitation/selector';
import { RedemptionError } from '../../components/RedemptionError';
import { RedemptionHeader } from '../../components/RedemptionHeader';
import { RedemptionInProgress } from '../../components/RedemptionInProgress';
import { RedemptionSuccess } from '../../components/RedemptionSuccess';
import { defaultError } from '../../constants';

import { AcceptQuotationStep } from './components/AcceptQuotationStep';
import { QuotationStep, QuotationStepTypes } from './components/QuotationStep';
import { QuotationStepLoading } from './components/QuotationStep/loading';
import { FixedIncomeRedemptionSteps } from './constants';

export interface FixedIncomeRedemptionProps {
  onClose: () => void;
}

export const FixedIncomeRedemption: React.FC<FixedIncomeRedemptionProps> = ({ onClose }) => {
  const navigation = useNavigation<SignedInNavigationScreenNavigationProp>();
  const { params } = useRoute<RouteProp<SignedInRootStackParamList, 'RedemptionSolicitation'>>();

  const { showToast, showToastFromError } = useToastCreator('fixed-income-redemption');
  const [step, setStep] = React.useState(1);
  const [quantitySelected, setQuantitySelected] = React.useState(0);
  const [isExecuting, setIsExecuting] = React.useState(false);

  const { data, error, refresher, loading } = useRecoilQuery(
    fixedIncomeWithdrawGetAsset({
      accountId: params.accountId,
      productId: params.productId,
    })
  );

  const hasQuote = !!params.quote;

  const { executeFixedIncomeRedemption, executeFixedIncomeAcceptQuotation } =
    useRedemptionActions();

  const handleFollowRedemption = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    }
    navigation.navigate('AppDrawer', {
      screen: 'CustomerDetailGroup',
      params: {
        screen: 'CustomerDetail',
        params: {
          ...params,
          selectedTab: 'transactions',
          movementInitialTab: 'FixedIncome',
        },
      },
    });
  };

  const handleConfirmQuotation: QuotationStepTypes.QuotationStepProps['onConfirm'] = (quantity) => {
    setIsExecuting(true);
    setQuantitySelected(quantity);

    executeFixedIncomeRedemption({
      clientDocument: params.cpfCnpj,
      uniqueCode: params.productId,
      quantity,
      accountId: params.accountId,
      interactionChannel: '',
      accountNumber: params.accountNumber,
    })
      .then(() => setStep(FixedIncomeRedemptionSteps.QUOTATION_SUCCESS))
      .catch((executeError) => {
        if (instanceOfErrorListResponse(executeError)) {
          (executeError as unknown as ErrorListResponse).errors?.forEach((errorItem) =>
            showToastFromError(errorItem)
          );
          return;
        }
        showToast(...defaultError);
      })
      .then(() => setIsExecuting(false));
  };

  const handleConfirmDirectRedemption: QuotationStepTypes.DirectRedemptionStepProps['onConfirm'] = (
    quantity,
    channel,
    observation
  ) => {
    setIsExecuting(true);
    setQuantitySelected(quantity);

    executeFixedIncomeRedemption({
      clientDocument: params.cpfCnpj,
      quantity,
      uniqueCode: params.productId,
      observation,
      interactionChannel: channel,
      accountId: params.accountId,
      accountNumber: params.accountNumber,
    })
      .then(() => setStep(FixedIncomeRedemptionSteps.REDEMPTION_SUCCESS))
      .catch((executeError) => {
        if (instanceOfErrorListResponse(executeError)) {
          (executeError as unknown as ErrorListResponse).errors?.forEach((errorItem) =>
            showToastFromError(errorItem)
          );
          return;
        }
        showToast(...defaultError);
      })
      .then(() => setIsExecuting(false));
  };

  const handleAcceptQuotation: QuotationStepTypes.ConfirmQuotationStepProps['onConfirm'] = (
    channel,
    observation
  ) => {
    setIsExecuting(true);

    executeFixedIncomeAcceptQuotation({
      quantity: params.quote?.quantity || 0,
      uniqueCode: params.quote?.uniqueCode || params.productId,
      quoteId: params.quote?.quotationId,
      observation,
      interactionChannel: channel,
      accountId: params.quote?.clientCode || params.accountId,
      accountNumber: params.accountNumber,
      withdrawId: params.quote?.withdrawId,
      originSystem: 'Genial',
    })
      .then(() => setStep(FixedIncomeRedemptionSteps.REDEMPTION_SUCCESS_QUOTATION))
      .catch((executeError) => {
        if (instanceOfErrorListResponse(executeError)) {
          (executeError as unknown as ErrorListResponse).errors?.forEach((errorItem) =>
            showToastFromError(errorItem)
          );
          return;
        }
        showToast(...defaultError);
      })
      .then(() => setIsExecuting(false));
  };

  const handleNextStep = () => {
    setStep(step + 1);
  };

  const handleRefresh = () => {
    refresher();
    setStep(1);
  };

  const renderStep = () => {
    if (step === FixedIncomeRedemptionSteps.ERROR) {
      return <RedemptionError onRefresh={handleRefresh} />;
    }
    if (loading || !data) {
      return <QuotationStepLoading productName={params.productName} isQuotation />;
    }

    switch (step) {
      case FixedIncomeRedemptionSteps.QUOTATION:
        return (
          <QuotationStep
            asset={data}
            {...(data.isDailyLiquidity
              ? { quotationType: 'directRedemption', onConfirm: handleConfirmDirectRedemption }
              : { quotationType: 'quotation', onConfirm: handleConfirmQuotation })}
            onCancel={onClose}
            redeemWithoutQuotation={data.isDailyLiquidity}
            isExecuting={isExecuting}
          />
        );

      case FixedIncomeRedemptionSteps.QUOTATION_SUCCESS:
        return (
          <RedemptionSuccess
            title="Cotação solicitada com sucesso."
            infos={[
              {
                label: 'Quantidade solicitada',
                value: `${quantitySelected} unidade${quantitySelected > 1 ? 's' : ''}`,
              },
              { label: 'Data de cotação', value: Utils.Mask.DateFormat(new Date(), 'dd/MM/yyyy') },
            ]}
            infoNote="O valor estimado para a cotação pode variar de acordo com as ofertas que o ativo receber."
            onClose={onClose}
            onFollowRedemption={handleNextStep}
          />
        );

      case FixedIncomeRedemptionSteps.REDEMPTION_SUCCESS:
        return (
          <RedemptionSuccess
            title="Resgate solicitado com sucesso."
            infos={[
              {
                label: 'Valor resgatado',
                value: `${Utils.Mask.Money(quantitySelected * (data.currentPrice || 0))}`,
              },
              { label: 'Data do resgate', value: Utils.Mask.DateFormat(new Date(), 'dd/MM/yyyy') },
            ]}
            infoNote="O valor estimado para a cotação pode variar de acordo com as ofertas que o ativo receber."
            onClose={onClose}
            onFollowRedemption={handleFollowRedemption}
          />
        );

      case FixedIncomeRedemptionSteps.ACCEPT_QUOTATION:
        return <AcceptQuotationStep asset={data} quote={params.quote!} onAccept={handleNextStep} />;

      case FixedIncomeRedemptionSteps.CONFIRM_QUOTATION:
        return (
          <QuotationStep
            asset={data}
            quotationType="confirmation"
            quote={params.quote!}
            onCancel={onClose}
            onConfirm={handleAcceptQuotation}
            isExecuting={isExecuting}
          />
        );

      case FixedIncomeRedemptionSteps.REDEMPTION_SUCCESS_QUOTATION:
        return (
          <RedemptionSuccess
            title="Resgate solicitado com sucesso."
            infos={[
              {
                label: 'Valor resgatado',
                value: `${Utils.Mask.Money(params.quote?.netValue || 0)}`,
              },
              { label: 'Data do resgate', value: Utils.Mask.DateFormat(new Date(), 'dd/MM/yyyy') },
            ]}
            onClose={onClose}
            onFollowRedemption={handleFollowRedemption}
          />
        );

      case FixedIncomeRedemptionSteps.QUOTATION_IN_PROGRESS:
        return (
          <RedemptionInProgress
            title="Cotação em andamento."
            infos={[
              {
                label: 'Quantidade solicitada',
                value: params.quote?.quantity.toString() || '0',
              },
              {
                label: 'Data de cotação',
                value: params.quote?.requestDate
                  ? Utils.Mask.DateFormat(params.quote?.requestDate, 'dd/MM/yyyy')
                  : '',
              },
            ]}
            infoNote="O valor estimado para a cotação pode variar de acordo com as ofertas que o ativo receber."
            onClose={onClose}
          />
        );

      default:
        return null;
    }
  };

  useEffect(() => {
    if (error) {
      setStep(FixedIncomeRedemptionSteps.ERROR);
    }
  }, [error]);

  useEffect(() => {
    if (hasQuote) {
      setStep(
        params.quote.status === 'PendingClient'
          ? FixedIncomeRedemptionSteps.ACCEPT_QUOTATION
          : FixedIncomeRedemptionSteps.QUOTATION_IN_PROGRESS
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasQuote]);

  return (
    <VStack space={Spacing['stack.xxxs']}>
      <RedemptionHeader onClose={onClose} />
      {renderStep()}
    </VStack>
  );
};
